import React from "react";
import { StyleSheet, View } from "react-native";

import CustomProgressBar from "../../../../../components/common/general/CustomProgressBar/CustomProgressBar";
import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors, radii, shadowStyles, spacing } from "../../../../../styles/design-system";

import type { GradeBreakdownRow } from "../RosterInsightGradesAndSubmissions.utils";
import { getDisplayNumber, getValueColor } from "../RosterInsightGradesAndSubmissions.utils";

interface RosterInsightGradeBreakdownCardProps {
  rows: GradeBreakdownRow[];
}

const RosterInsightGradeBreakdownCard = ({ rows }: RosterInsightGradeBreakdownCardProps) => {
  return (
    <View style={[styles.breakdownCard, shadowStyles.sm]}>
      <CustomText
        text="Grade Breakdown"
        size="m"
        weight="bold"
        useTranslationText={false}
        style={styles.breakdownTitle}
      />

      <View style={styles.breakdownRows}>
        {rows.map((row) => (
          <View key={row.label} style={styles.breakdownRow}>
            <CustomText text={row.label} size="s" useTranslationText={false} style={styles.breakdownLabel} />

            <View style={styles.breakdownBarContainer}>
              <CustomProgressBar
                progress={row.percentage}
                color={row.progressColor}
                unfilledColor={colors.grey[200]}
                ariaLabel={`${row.label} grade breakdown`}
              />
            </View>

            <View style={styles.breakdownValueContainer}>
              <CustomText
                text={row.percentage != null ? `${row.percentage}%` : "—"}
                size="s"
                weight="bold"
                useTranslationText={false}
                style={[styles.breakdownPercentage, { color: getValueColor(row.percentage, colors.grey[900]) }]}
              />
              {row.earned != null && row.possible != null ? (
                <CustomText
                  text={`(${getDisplayNumber(row.earned)}/${getDisplayNumber(row.possible)})`}
                  size="xs"
                  useTranslationText={false}
                  style={styles.breakdownFraction}
                />
              ) : null}
            </View>
          </View>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  breakdownCard: {
    backgroundColor: colors.white,
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: colors.grey[200],
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xl,
  },
  breakdownTitle: {
    color: colors.grey[900],
  },
  breakdownRows: {
    marginTop: spacing.lg,
    gap: spacing.md,
  },
  breakdownRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.md,
  },
  breakdownLabel: {
    width: 136,
    color: colors.grey[700],
  },
  breakdownBarContainer: {
    flex: 1,
  },
  breakdownValueContainer: {
    minWidth: 96,
    maxWidth: 120,
    alignItems: "flex-end",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
    justifyContent: "flex-end",
  },
  breakdownPercentage: {
    color: colors.grey[900],
  },
  breakdownFraction: {
    color: colors.grey[600],
  },
});

export default RosterInsightGradeBreakdownCard;
